import { createTheme } from '@mui/material/styles';

// =============================================================================
// Berchtold & Cie – Traditional Swiss Private Bank
// Clean, clinical, light. Serif elegance with razor-sharp geometry.
// Navy on white, red accent — old money Swiss banking.
// =============================================================================

const swissTheme = createTheme({
  palette: {
    mode: 'light',
    logo: 'light',
    bank: {
      name: 'Berchtold & Cie',
      logo: '/bank-logos/berchtold-cie.png',
    },
    primary: {
      main: '#0a2e5c',
      dark: '#08244a',
      light: '#1a4a7a',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#c41230',
      dark: '#a00e28',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8f9fb',
      paper: '#ffffff',
      surface: '#ffffff',
      gradient: 'linear-gradient(180deg, #f8f9fb 0%, #eef1f5 50%, #f8f9fb 100%)',
    },
    text: {
      primary: '#0a2e5c',
      secondary: '#4a6180',
      disabled: '#8a9bb0',
      hint: '#a0b0c0',
      dark: '#0a1929',
      body: '#2c3e50',
      heading: '#0a2e5c',
    },
    success: {
      main: '#1a8a4a',
      light: '#e8f5ec',
      dark: '#0d5c2e',
      border: '#b8dfc8',
    },
    error: {
      main: '#c41230',
      light: '#fce8ec',
    },
    grey: {
      50: '#f8f9fb',
      100: '#f4f5f7',
      200: '#e4e8ed',
      300: '#dde3ea',
      400: '#c8d0da',
      500: '#a0b0c0',
      600: '#8a9bb0',
      700: '#6a7f96',
      800: '#4a6180',
      900: '#2c3e50',
    },
    divider: 'rgba(10, 46, 92, 0.08)',
    action: {
      hover: 'rgba(10, 46, 92, 0.04)',
    },
    brand: {
      cyan: '#0a2e5c',
      cyanHover: '#08244a',
      cyanLight: '#1a4a7a',
      navy: '#f8f9fb',
      navyLight: '#ffffff',
      navyMid: '#eef1f5',
      dialogBg: '#ffffff',
    },
    border: {
      light: '#dde3ea',
      medium: '#c8d0da',
      dark: '#b0bac8',
      input: '#8a9bb0',
      muted: '#c8d0da',
      tableCell: 'rgba(10, 46, 92, 0.08)',
      onDark: 'rgba(10, 46, 92, 0.06)',
      onDarkAlt: 'rgba(10, 46, 92, 0.10)',
    },
    voice: {
      initBg1: '#eef1f5',
      initBg2: '#dde3ea',
      initBg3: '#c8d0da',
      thinkBg1: '#fef8e8',
      thinkBg2: '#fdf0c8',
      thinkBg3: '#fce8a8',
      speakBg1: '#e8f5ec',
      speakBg2: '#c8e8d4',
      speakBg3: '#a8dab8',
      overlayBg: 'linear-gradient(180deg, #f8f9fb 0%, #eef1f5 50%, #f8f9fb 100%)',
    },
    gradient: {
      avatar: 'linear-gradient(135deg, #0a2e5c 0%, #1a4a7a 100%)',
      voiceButton: 'linear-gradient(135deg, #eef1f5 0%, #dde3ea 100%)',
      voiceTooltip: 'linear-gradient(135deg, #0a2e5c 0%, #1a4a7a 100%)',
    },
  },
  typography: {
    fontFamily: "'Cormorant Garant', 'Georgia', serif",
    h1: { fontSize: '42px', fontWeight: 300, letterSpacing: '-0.5px' },
    h2: { fontSize: '23px', fontWeight: 500 },
    h3: { fontSize: '21px', fontWeight: 500 },
    h4: { fontSize: '19px', fontWeight: 500 },
    h5: { fontSize: '17px', fontWeight: 500 },
    h6: { fontSize: '15px', fontWeight: 500 },
    body1: { fontSize: '14px', fontWeight: 400 },
    body2: { fontSize: '13px', fontWeight: 400 },
    caption: { fontSize: '12px', fontWeight: 400 },
    overline: { fontSize: '11px', fontWeight: 500, letterSpacing: '1px', textTransform: 'uppercase' },
    button: { fontSize: '14px', fontWeight: 500, textTransform: 'none' },
  },
  shape: {
    borderRadius: 4,
    radiusSmall: 2,
    radiusMedium: 3,
    radiusLarge: 6,
    radiusXl: 8,
    radiusXxl: 10,
    radiusPill: 20,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(10, 46, 92, 0.04)',
    '0 2px 8px rgba(10, 46, 92, 0.08)',
    '0 1px 4px rgba(10, 46, 92, 0.06)',
    '0 2px 6px rgba(10, 46, 92, 0.10)',
    '0 4px 16px rgba(10, 46, 92, 0.10)',
    '0 1px 6px rgba(10, 46, 92, 0.06)',
    '0 2px 12px rgba(196, 18, 48, 0.15)',
    '0 4px 16px rgba(196, 18, 48, 0.20)',
    '0 0 40px rgba(10, 46, 92, 0.10)',
    '0 0 80px rgba(10, 46, 92, 0.08)',
    'inset 0 0 20px rgba(10, 46, 92, 0.05)',
    ...Array(13).fill('none'),
  ],
  spacing: 8,
  breakpoints: {
    values: { xs: 0, sm: 600, md: 768, lg: 1068, xl: 1536 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          fontFamily: "'Cormorant Garant', 'Georgia', serif",
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 4, textTransform: 'none', fontFamily: "'Cormorant Garant', 'Georgia', serif" },
        containedPrimary: {
          backgroundColor: '#0a2e5c',
          '&:hover': { backgroundColor: '#08244a' },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: { fontFamily: "'Cormorant Garant', 'Georgia', serif" },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: '#ffffff',
          backgroundImage: 'none',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: '#ffffff',
          backgroundImage: 'none',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          background: '#ffffff',
          backgroundImage: 'none',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#0a2e5c' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Cormorant Garant', 'Georgia', serif",
          '&.Mui-selected': { color: '#0a2e5c' },
        },
      },
    },
  },
});

export default swissTheme;
